import { useMemo, useState } from "react";
import type { CatalogItem } from "../../api/types";
import {
  ALL_ZONES,
  filterProductsByZone,
  productZoneGroups,
  resolveZoneFilter,
  sortInventoryProducts,
  type InventoryProductRow,
  type InventoryProductSortKey,
  type InventoryZoneFilter,
} from "../../utils/inventoryProducts";
import { Chip } from "../ui/Chip";
import { ItemThumb } from "../ItemThumb";
import type { InventoryCardTarget } from "./InventoryItemCard";
import { InventoryStockCard } from "./InventoryStockCard";

const DIR_LABEL: Record<InventoryProductSortKey, Record<"asc" | "desc", string>> = {
  name: { asc: "A→Z", desc: "Z→A" },
  qty: { asc: "Low first", desc: "High first" },
  lastIn: { asc: "Oldest first", desc: "Newest first" },
};

/**
 * G-132 — the stock list: the "Where" chips, the result line with its sort, one
 * row per product, then the catalogue items this view holds none of.
 *
 * One list at every width. G-92's phone cards and the per-zone desktop tables
 * were two renderings of one question; this is one rendering, and the desktop
 * only widens each row (CSS).
 */
export function InventoryCardList({
  products,
  notStockedHere,
  byShop,
  lowItemIds,
  onThumbnailClick,
  searching,
  onOpenItem,
}: {
  /** One row per product, already filtered by search and category. */
  products: InventoryProductRow[];
  /** G-89's catalogue matches the shop holds none of. */
  notStockedHere: CatalogItem[];
  /** The rows span several shops: tag by shop, not by zone. */
  byShop: boolean;
  /** Items on the low-stock list: their total reads in the warning colour. */
  lowItemIds?: ReadonlySet<string>;
  /** Takes the item, not the product: the zero list has no product. */
  onThumbnailClick: (item: { id: string; name: string; imageKey?: string | null }) => void;
  searching: boolean;
  /** The panel owns the item card; this list only says which one was tapped. */
  onOpenItem: (target: InventoryCardTarget) => void;
}) {
  const [zone, setZone] = useState<InventoryZoneFilter>(ALL_ZONES);
  const [sortKey, setSortKey] = useState<InventoryProductSortKey>("name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const groups = useMemo(() => productZoneGroups(products), [products]);
  // A search or a category change can empty the chosen area out from under its
  // chip; falling back to All beats an empty list with no explanation.
  const activeZone = resolveZoneFilter(zone, groups);

  const visible = useMemo(
    () => sortInventoryProducts(filterProductsByZone(products, activeZone), sortKey, sortDir),
    [products, activeZone, sortKey, sortDir],
  );

  const dirLabel = DIR_LABEL[sortKey][sortDir];

  // "All" plus one zone group is no choice at all: the row is shown only when
  // there is somewhere else to look.
  const showWhere = groups.length > 2;

  return (
    <div className="inv-cards">
      {showWhere ? (
        <div className="inv-where" role="group" aria-label="Where">
          <span className="inv-where-label">Where:</span>
          {groups.map((group) => (
            <Chip
              key={group.key}
              label={group.label}
              count={group.count}
              selected={activeZone === group.key}
              onClick={() => setZone(group.key)}
            />
          ))}
        </div>
      ) : null}

      <div className="inv-sort-row">
        <span>
          {visible.length} {visible.length === 1 ? "product" : "products"}
        </span>
        <div className="inv-sort-controls">
          <button
            type="button"
            className="inv-sort-btn"
            aria-pressed={sortKey === "name"}
            onClick={() => setSortKey("name")}
          >
            Name
          </button>
          <button
            type="button"
            className="inv-sort-btn"
            aria-pressed={sortKey === "qty"}
            onClick={() => setSortKey("qty")}
          >
            Quantity
          </button>
          <button
            type="button"
            className="inv-sort-btn"
            aria-pressed={sortKey === "lastIn"}
            onClick={() => {
              // The question behind this sort is "what came in lately?", so it
              // opens newest first; the direction button still flips it.
              if (sortKey !== "lastIn") setSortDir("desc");
              setSortKey("lastIn");
            }}
          >
            Last received
          </button>
          <button
            type="button"
            className="inv-sort-btn"
            onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
          >
            {dirLabel}
          </button>
        </div>
      </div>

      {visible.length === 0 ? (
        <p className="muted inv-empty">
          {searching ? "Nothing here matches your search." : "No stock in this area."}
        </p>
      ) : (
        <div className="inv-card-list">
          {visible.map((product) => (
            <InventoryStockCard
              key={product.item.id}
              product={product}
              shownQty={product.zoneQty}
              byShop={byShop}
              isLow={lowItemIds?.has(product.item.id)}
              onOpen={(p) => onOpenItem({ kind: "stocked", product: p })}
              onThumbnailClick={onThumbnailClick}
            />
          ))}
        </div>
      )}

      {notStockedHere.length > 0 ? (
        <div className="inv-zero-group">
          <h3 className="inv-group-head">Not in this shop yet</h3>
          {notStockedHere.map((item) => (
            <div key={item.id} className="inv-card is-zero-row">
              {/*
                * The thumbnail is not decoration here: `.inv-card` lays out
                * its slots by position, and a row with fewer collapsed the
                * grid and pushed the quantity outside the card (found by
                * /code-review, G-92). It also matches the stocked rows.
                */}
              <div className="inv-card-thumb">
                <ItemThumb
                  itemId={item.id}
                  imageKey={item.imageKey ?? null}
                  alt={item.name}
                  onThumbnailClick={() => onThumbnailClick(item)}
                />
              </div>
              {/*
                * G-94: these rows used to be dead text. They are the rows most
                * likely to need an action — an item the shop does not carry is
                * usually one that just arrived — so they open the same card the
                * stocked rows do, which leads with Receive when there is
                * nothing on hand. A real <button>, so Tab and a screen reader
                * find it.
                */}
              <button
                type="button"
                className="inv-card-body"
                onClick={() => onOpenItem({ kind: "not-stocked", item })}
              >
                <span className="inv-card-name">{item.name}</span>
                <span className="inv-card-meta">
                  <span className="inv-card-tag">{item.category.label}</span>
                </span>
              </button>
              <span className="inv-card-cost" />
              <span className="inv-card-lastin" />
              <div className="inv-card-qty is-zero" aria-label={`0 in stock, ${item.name}`}>
                <b>0</b>
                <span>{item.defaultUom}</span>
              </div>
            </div>
          ))}
          <p className="muted inv-zero-hint">
            Tap one to receive it, or bring it in on a delivery.
          </p>
        </div>
      ) : null}
    </div>
  );
}
